import type { ILibraryFolder } from "@/api";
import { getAncestorChain } from "@/pages/frontend/documents/utils/folderTreeUtils";

import type { IBreadcrumbSegment } from "./DocumentPicker.types";

interface BuildClientRootedBreadcrumbsArgs {
    folders: ILibraryFolder[];
    /** Client root folder id; null = no workspace root materialized yet. */
    rootFolderId: number | null;
    /** The folder currently in view (equals rootFolderId at the root). */
    currentFolderId: number | null;
    /** The client's display name — the root crumb's label. */
    clientName?: string;
    searchMode: boolean;
    onNavigate: (folderId: number | null) => void;
}

/**
 * Client-rooted breadcrumbs shared by the workspace Library tab and the
 * strictly client-scoped document picker (the Unit 7 FilterBar seam): root
 * crumb = the client name, ancestors navigate within the client subtree, the
 * root crumb clears to the client root — zero router navigation, so no crumb
 * can exit to the wider library. Search mode collapses to a single
 * non-navigable "Search results" crumb (results are flat).
 *
 * Each crumb's `folderId` is its drop target; the root crumb's resolves to the
 * client root id, NEVER null (a null drop would strip the client tag, Unit 8),
 * and stays undefined for the "Search results" crumb (never a drop target).
 */
export function buildClientRootedBreadcrumbs({
    folders,
    rootFolderId,
    currentFolderId,
    clientName,
    searchMode,
    onNavigate,
}: BuildClientRootedBreadcrumbsArgs): IBreadcrumbSegment[] {
    if (searchMode) {
        return [
            { label: "Search results", onClick: () => {}, isCurrent: true },
        ];
    }
    const clientLabel = clientName ?? "…";
    const rootSegment = (isCurrent: boolean): IBreadcrumbSegment => ({
        label: clientLabel,
        onClick: isCurrent ? () => {} : () => onNavigate(null),
        isCurrent,
        folderId: rootFolderId ?? undefined,
    });
    if (
        rootFolderId === null ||
        currentFolderId === null ||
        currentFolderId === rootFolderId
    ) {
        return [rootSegment(true)];
    }
    const current = folders.find((f) => f.id === currentFolderId);
    if (!current) return [rootSegment(true)];
    // [root..current]; the chain terminates at the client root by construction.
    const chain = getAncestorChain(folders, current);
    return [
        rootSegment(false),
        ...chain.slice(1, -1).map((f) => ({
            label: f.name,
            onClick: () => onNavigate(f.id),
            isCurrent: false,
            folderId: f.id,
        })),
        {
            label: current.name,
            onClick: () => {},
            isCurrent: true,
            folderId: current.id,
        },
    ];
}
